import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import Container from '../components/ui/Container';
import FooterPageContent from '../components/layout/FooterPageContent';
import { api } from '../services/apiClient';

const WholesalePage = () => {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState(new Set());

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await api.events.list();
        console.log('Events loaded:', data);
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, []);

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const shouldTruncate = (description) => {
    return description.length > 150;
  };

  const getTruncatedDescription = (description) => {
    if (description.length <= 150) return description;
    return description.substring(0, 150) + '...';
  };
  const pageStyles = {
    padding: `${theme.spacing.xxl} 0`,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
  };

  const contentStyles = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.lg}`,
  };

  const headingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xxl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const subHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primaryDark,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.md,
  };

  const paragraphStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const contactBoxStyles = {
    backgroundColor: theme.colors.neutral,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.lg,
  };

  const highlightBoxStyles = {
    backgroundColor: theme.colors.primaryLight,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.lg,
    borderLeft: `4px solid ${theme.colors.primary}`,
  };

  const eventsContainerStyles = {
    marginBottom: theme.spacing.xxl,
    position: 'relative',
  };

  const eventsScrollContainerStyles = {
    display: 'flex',
    gap: theme.spacing.xl,
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: theme.spacing.lg,
    paddingTop: theme.spacing.sm,
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
    msOverflowStyle: '-ms-autohiding-scrollbar',
  };

  const eventBoxStyles = {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    border: 'none',
    padding: theme.spacing.xl,
    borderRadius: '16px',
    minWidth: '340px',
    maxWidth: '420px',
    flexShrink: 0,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
  };

  const eventAccentBarStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark || theme.colors.primary} 100%)`,
  };

  const eventTitleStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: '1.375rem',
    fontWeight: '700',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    marginTop: 0,
    lineHeight: '1.3',
  };

  const eventMetaContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  };

  const eventMetaItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
  };

  const eventDescriptionStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: '1.6',
    color: theme.colors.text,
    marginBottom: 0,
    marginTop: 0,
  };

  const showMoreButtonStyles = {
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    marginTop: theme.spacing.sm,
    textDecoration: 'underline',
    transition: 'color 0.2s ease',
  };

  const eventsSectionTitleStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: '1.75rem',
    fontWeight: '700',
    color: theme.colors.text,
    marginBottom: theme.spacing.lg,
    marginTop: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  return (
    <div style={pageStyles}>
      <Container>
        <div style={contentStyles}>
          <h1 style={headingStyles}>Wholesale Information</h1>

          {/* Events Section - Always shown at the top */}
          {loadingEvents && (
            <div style={{ marginBottom: theme.spacing.lg, color: theme.colors.textSecondary }}>
              Loading events...
            </div>
          )}
          {!loadingEvents && events.length > 0 && (
            <div style={eventsContainerStyles}>
              <h2 style={eventsSectionTitleStyles}>
                Upcoming Events
              </h2>
              <style>
                {`
                  .events-scroll-container::-webkit-scrollbar {
                    height: 10px;
                  }
                  .events-scroll-container::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 10px;
                    margin: 0 20px;
                  }
                  .events-scroll-container::-webkit-scrollbar-thumb {
                    background: linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark || theme.colors.primary} 100%);
                    border-radius: 10px;
                  }
                  .events-scroll-container::-webkit-scrollbar-thumb:hover {
                    background: ${theme.colors.primaryDark || theme.colors.primary};
                  }
                  .event-card {
                    cursor: default;
                  }
                  .event-card:hover {
                    transform: translateY(-8px) scale(1.02);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
                  }
                  .show-more-btn:hover {
                    color: ${theme.colors.primaryDark || theme.colors.primary};
                  }
                `}
              </style>
              <div className="events-scroll-container" style={eventsScrollContainerStyles}>
                {events.map((event) => {
                  const isExpanded = expandedEvents.has(event.id);
                  const needsTruncation = shouldTruncate(event.description);
                  const displayDescription = isExpanded || !needsTruncation 
                    ? event.description 
                    : getTruncatedDescription(event.description);

                  return (
                    <div key={event.id} className="event-card" style={eventBoxStyles}>
                      <div style={eventAccentBarStyles}></div>
                      <h3 style={eventTitleStyles}>{event.title}</h3>
                      <div style={eventMetaContainerStyles}>
                        <div style={eventMetaItemStyles}>
                          <span style={{ fontWeight: 600, color: theme.colors.text }}>Date:</span>
                          <span style={{ fontWeight: 600 }}>{event.event_date}</span>
                        </div>
                        <div style={eventMetaItemStyles}>
                          <span style={{ fontWeight: 600, color: theme.colors.text }}>Location:</span>
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              color: theme.colors.primary,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              transition: 'color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.color = theme.colors.primaryDark || theme.colors.primary}
                            onMouseLeave={(e) => e.target.style.color = theme.colors.primary}
                          >
                            {event.location}
                          </a>
                        </div>
                      </div>
                      <p style={eventDescriptionStyles}>{displayDescription}</p>
                      {needsTruncation && (
                        <button
                          className="show-more-btn"
                          style={showMoreButtonStyles}
                          onClick={() => toggleEventExpansion(event.id)}
                        >
                          {isExpanded ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!loadingEvents && events.length === 0 && (
            <div style={{ marginBottom: theme.spacing.lg, color: theme.colors.textSecondary, fontSize: theme.typography.fontSize.sm }}>
              {/* No events to display - this is hidden for production */}
            </div>
          )}

          <FooterPageContent slug="wholesale">
          <p style={paragraphStyles}>
            BS Trading Co. is proud to offer wholesale pricing to qualified businesses, retailers, 
            interior designers, and bulk buyers. Partner with us to access premium Western goods, 
            cowhides, furniture, furs, and rugs at competitive wholesale rates.
          </p>

          <h2 style={subHeadingStyles}>Why Choose BS Trading Co. for Wholesale?</h2>
          <ul style={{
            fontSize: theme.typography.fontSize.base,
            lineHeight: theme.typography.lineHeight.relaxed,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
            paddingLeft: theme.spacing.xl,
          }}>
            <li>Competitive wholesale pricing on bulk orders</li>
            <li>Extensive inventory of quality Western products</li>
            <li>Reliable sourcing and consistent supply</li>
            <li>Flexible ordering options</li>
            <li>Experienced staff to assist with product selection</li>
            <li>Fast shipping and delivery options</li>
            <li>Custom orders available for large quantities</li>
          </ul>

          <h2 style={subHeadingStyles}>Who Qualifies for Wholesale Pricing?</h2>
          <p style={paragraphStyles}>
            We work with a variety of businesses and professionals, including:
          </p>
          <ul style={{
            fontSize: theme.typography.fontSize.base,
            lineHeight: theme.typography.lineHeight.relaxed,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
            paddingLeft: theme.spacing.xl,
          }}>
            <li>Retail stores and Western boutiques</li>
            <li>Interior designers and decorators</li>
            <li>Furniture stores and showrooms</li>
            <li>Online retailers and e-commerce businesses</li>
            <li>Hotels, lodges, and hospitality businesses</li>
            <li>Event planners and staging companies</li>
            <li>Ranches and agricultural businesses</li>
          </ul>

          <h2 style={subHeadingStyles}>How to Apply for Wholesale Account</h2>
          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.sm }}>
              <strong>Getting started is easy!</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              Contact us directly to discuss your wholesale needs. We'll work with you to set up 
              an account and provide pricing information based on your order volume and requirements.
            </p>
          </div>

          <h2 style={subHeadingStyles}>Wholesale Benefits</h2>
          <p style={paragraphStyles}>
            When you become a wholesale partner with BS Trading Co., you'll enjoy:
          </p>
          <ul style={{
            fontSize: theme.typography.fontSize.base,
            lineHeight: theme.typography.lineHeight.relaxed,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
            paddingLeft: theme.spacing.xl,
          }}>
            <li>Volume-based discount pricing</li>
            <li>Dedicated account support</li>
            <li>Priority access to new inventory</li>
            <li>Flexible payment terms (for qualified accounts)</li>
            <li>Customized ordering solutions</li>
            <li>Product expertise and recommendations</li>
          </ul>

          <h2 style={subHeadingStyles}>Minimum Order Requirements</h2>
          <p style={paragraphStyles}>
            Minimum order quantities and values vary depending on the product category and your 
            specific needs. Contact us to discuss requirements for your business.
          </p>

          <h2 style={subHeadingStyles}>Custom Orders & Special Requests</h2>
          <p style={paragraphStyles}>
            Need something specific? We can often accommodate custom orders for large quantities, 
            including special sizes, colors, or product specifications. Our team will work with 
            you to source exactly what you need.
          </p>

          <h2 style={subHeadingStyles}>Get Started Today</h2>
          <p style={paragraphStyles}>
            Ready to partner with BS Trading Co.? Contact our wholesale team to discuss your 
            needs and learn more about our pricing and programs.
          </p>

          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co. - Wholesale Department</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> <a href="tel:8174474433" style={{ color: theme.colors.primary }}>817-447-4433</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> <a href="tel:18005540910" style={{ color: theme.colors.primary }}>1-800-554-0910</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM CST
            </p>
          </div>

          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Note:</strong> Please mention that you're inquiring about wholesale pricing 
              when you contact us, and have your business information ready to expedite the process.
            </p>
          </div>
          </FooterPageContent>
        </div>
      </Container>
    </div>
  );
};

export default WholesalePage;

